import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  getMerchantDefaultRoute,
  useMerchantAuthActions,
} from "../auth/MerchantAuthContext";
import { buildMerchantSession } from "../auth/merchantAuthSession";
import {
  fetchMerchantContext,
  getMerchantApiErrorMessage,
  merchantLogin,
} from "../data/merchantApi";
import {
  clearStoredMerchantPortalSession,
  setStoredMerchantPortalToken,
} from "../auth/merchantAuthSession";

const LOGIN_BACKGROUND_BLOBS = [
  {
    "--merchant-login-blob-color": "rgba(8, 34, 76, 0.46)",
    "--merchant-login-blob-blur": "96px",
    "--merchant-login-blob-opacity": "1",
    "--merchant-login-blob-animation": "merchantLoginBlobA",
    "--merchant-login-blob-duration": "16s",
    width: "540px",
    height: "540px",
    top: "-12%",
    left: "-10%",
  },
  {
    "--merchant-login-blob-color": "rgba(30, 72, 150, 0.38)",
    "--merchant-login-blob-blur": "88px",
    "--merchant-login-blob-opacity": "0.96",
    "--merchant-login-blob-animation": "merchantLoginBlobB",
    "--merchant-login-blob-duration": "15s",
    width: "500px",
    height: "500px",
    top: "48%",
    right: "-8%",
  },
  {
    "--merchant-login-blob-color": "rgba(164, 203, 255, 0.24)",
    "--merchant-login-blob-blur": "118px",
    "--merchant-login-blob-opacity": "0.82",
    "--merchant-login-blob-animation": "merchantLoginBlobC",
    "--merchant-login-blob-duration": "18s",
    width: "620px",
    height: "620px",
    top: "18%",
    left: "30%",
  },
  {
    "--merchant-login-blob-color": "rgba(17, 53, 118, 0.38)",
    "--merchant-login-blob-blur": "80px",
    "--merchant-login-blob-opacity": "0.92",
    "--merchant-login-blob-animation": "merchantLoginBlobD",
    "--merchant-login-blob-duration": "17s",
    width: "470px",
    height: "470px",
    bottom: "-10%",
    left: "8%",
  },
  {
    "--merchant-login-blob-color": "rgba(206, 229, 255, 0.18)",
    "--merchant-login-blob-blur": "96px",
    "--merchant-login-blob-opacity": "0.74",
    "--merchant-login-blob-animation": "merchantLoginBlobE",
    "--merchant-login-blob-duration": "19s",
    width: "430px",
    height: "430px",
    top: "2%",
    right: "14%",
  },
];

const LOGIN_SUCCESS_TRANSITION_MS = 650;

function buildLoginNoise() {
  const permutation = new Uint8Array(512);
  const base = new Uint8Array(256);

  for (let index = 0; index < 256; index += 1) {
    base[index] = index;
  }

  for (let index = 255; index > 0; index -= 1) {
    const swapIndex = (Math.random() * (index + 1)) | 0;
    const current = base[index];
    base[index] = base[swapIndex];
    base[swapIndex] = current;
  }

  for (let index = 0; index < 512; index += 1) {
    permutation[index] = base[index & 255];
  }

  const gradients = [
    [1, 1],
    [-1, 1],
    [1, -1],
    [-1, -1],
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];

  function fade(value) {
    return value * value * value * (value * (value * 6 - 15) + 10);
  }

  function lerp(start, end, amount) {
    return start + (end - start) * amount;
  }

  function dot(gradient, x, y) {
    return gradient[0] * x + gradient[1] * y;
  }

  return function noise(x, y) {
    const xFloor = Math.floor(x);
    const yFloor = Math.floor(y);
    const xHash = xFloor & 255;
    const yHash = yFloor & 255;
    const localX = x - xFloor;
    const localY = y - yFloor;
    const u = fade(localX);
    const v = fade(localY);
    const a = permutation[xHash] + yHash;
    const b = permutation[xHash + 1] + yHash;

    return lerp(
      lerp(
        dot(gradients[permutation[a] & 7], localX, localY),
        dot(gradients[permutation[b] & 7], localX - 1, localY),
        u,
      ),
      lerp(
        dot(gradients[permutation[a + 1] & 7], localX, localY - 1),
        dot(gradients[permutation[b + 1] & 7], localX - 1, localY - 1),
        u,
      ),
      v,
    );
  };
}

function MerchantLoginField(props) {
  const {
    autoComplete,
    id,
    label,
    onChange,
    type,
    value,
  } = props;

  return (
    <div className="merchant-form-group">
      <label className="merchant-form-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="merchant-form-input merchant-login-input"
        type={type}
        value={value}
        autoComplete={autoComplete}
        onChange={onChange}
        required
      />
    </div>
  );
}

export default function MerchantLoginPage() {
  const history = useHistory();
  const location = useLocation();
  const { login } = useMerchantAuthActions();
  const canvasRef = React.useRef(null);
  const isEnteringRef = React.useRef(false);
  const [account, setAccount] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isEntering, setIsEntering] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  React.useEffect(function syncEnteringRef() {
    isEnteringRef.current = isEntering;
  }, [isEntering]);

  React.useEffect(function setupLoginBackgroundParticles() {
    const canvas = canvasRef.current;

    if (!canvas) {
      return undefined;
    }

    const context = canvas.getContext("2d");

    if (!context) {
      return undefined;
    }

    const noise = buildLoginNoise();

    const reducedMotionQuery = window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;

    let animationFrameId = 0;
    let width = 0;
    let height = 0;
    let particles = [];
    let tick = 0;
    let isDisposed = false;
    let enteringStartTick = null;

    function buildParticles(nextWidth, nextHeight) {
      particles = [];
      const count = Math.min(7800, Math.max(3400, Math.floor((nextWidth * nextHeight) / 270)));
      const columns = Math.max(
        42,
        Math.floor(Math.sqrt(count * (nextWidth / Math.max(nextHeight, 1)))),
      );
      const rows = Math.max(30, Math.floor(count / Math.max(columns, 1)));
      const gapX = nextWidth / columns;
      const gapY = nextHeight / rows;

      for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
          const originX = column * gapX + gapX * (0.28 + Math.random() * 0.44);
          const originY = row * gapY + gapY * (0.22 + Math.random() * 0.56);
          const hueDrift = Math.random();

          particles.push({
            ox: originX,
            oy: originY,
            x: originX,
            y: originY,
            vx: 0,
            vy: 0,
            size: 0.85 + Math.random() * 2.15,
            alpha: 0.16 + Math.random() * 0.42,
            phase: Math.random() * Math.PI * 2,
            color: "rgba(" +
              Math.round(18 + hueDrift * 18) +
              ", " +
              Math.round(64 + hueDrift * 44) +
              ", " +
              Math.round(160 + hueDrift * 64) +
              ", 0.96)",
          });
        }
      }
    }

    function resizeCanvas() {
      const nextWidth = window.innerWidth;
      const nextHeight = window.innerHeight;
      const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);

      width = nextWidth;
      height = nextHeight;
      canvas.width = Math.round(nextWidth * devicePixelRatio);
      canvas.height = Math.round(nextHeight * devicePixelRatio);
      canvas.style.width = nextWidth + "px";
      canvas.style.height = nextHeight + "px";
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
      buildParticles(nextWidth, nextHeight);
    }

    function getFlowAngle(x, y, time) {
      let value = 0;
      let amplitude = 1;
      let frequency = 1;

      for (let index = 0; index < 3; index += 1) {
        value += noise(x * 0.0018 * frequency + time * 0.14, y * 0.0018 * frequency + time * 0.11) * amplitude;
        amplitude *= 0.5;
        frequency *= 2;
      }

      return value * Math.PI * 4;
    }

    function drawParticles() {
      if (isDisposed) {
        return;
      }

      tick += 1;
      const isEnteringActive = isEnteringRef.current;
      const burstX = width * 0.5;
      const burstY = height * 0.52;
      const time = tick * 0.009;
      let transitionProgress = 0;

      if (isEnteringActive) {
        if (enteringStartTick === null) {
          enteringStartTick = tick;
        }

        transitionProgress = Math.min((tick - enteringStartTick) / 220, 1);
      } else {
        enteringStartTick = null;
      }

      context.clearRect(0, 0, width, height);
      context.save();
      context.globalCompositeOperation = "screen";

      particles.forEach(function drawParticle(particle) {
        const angle = getFlowAngle(particle.ox, particle.oy, time + particle.phase);

        if (isEnteringActive) {
          const dx = particle.x - burstX;
          const dy = particle.y - burstY;
          const distance = Math.hypot(dx, dy) || 1;
          const radialX = dx / distance;
          const radialY = dy / distance;
          const swirlX = -radialY;
          const swirlY = radialX;
          const noiseX = Math.cos(angle);
          const noiseY = Math.sin(angle);
          const falloff = Math.max(0, 1 - distance / (Math.max(width, height) * 0.88));
          const surgeWave = 1 - (1 - transitionProgress) * (1 - transitionProgress);
          const swirlForce = (1.8 + falloff * 3.2) * (0.9 + surgeWave * 1.1);
          const swellForce = (0.9 + falloff * 1.9) * (0.88 + surgeWave * 1.2);

          particle.vx += (swirlX * 4.1 + noiseX * 2.4 + radialX * 1.45) * swirlForce * 0.14;
          particle.vy += (swirlY * 4.1 + noiseY * 2.4 + radialY * 1.45) * swirlForce * 0.14;
          particle.vx += (noiseX * 1.8 + radialX * 2.6) * swellForce * 0.14;
          particle.vy += (noiseY * 1.8 + radialY * 2.6) * swellForce * 0.14;
          particle.vx *= transitionProgress < 0.72 ? 0.953 : 0.971;
          particle.vy *= transitionProgress < 0.72 ? 0.953 : 0.971;
          particle.x += particle.vx;
          particle.y += particle.vy;
        } else {
          const idleAngle = getFlowAngle(
            particle.ox * 1.15 + particle.phase * 58,
            particle.oy * 1.1 - particle.phase * 36,
            time * 0.62 + particle.phase * 0.42,
          );
          const idleSpeed = 0.1 + particle.alpha * 0.085;
          const centerDx = particle.x - width / 2;
          const centerDy = particle.y - height / 2;
          const centerDistance = Math.hypot(centerDx, centerDy) || 1;
          const swirlX = -centerDy / centerDistance;
          const swirlY = centerDx / centerDistance;
          const idleReach = 12 + particle.alpha * 11;
          const targetX = particle.ox + Math.cos(idleAngle) * idleReach;
          const targetY = particle.oy + Math.sin(idleAngle) * idleReach;

          particle.vx *= 0.93;
          particle.vy *= 0.93;
          particle.vx += (Math.cos(idleAngle) * 0.66 + swirlX * 0.42) * idleSpeed;
          particle.vy += (Math.sin(idleAngle) * 0.66 + swirlY * 0.42) * idleSpeed;
          particle.vx += (targetX - particle.x) * 0.028;
          particle.vy += (targetY - particle.y) * 0.028;
          particle.vx += (particle.ox - particle.x) * 0.01;
          particle.vy += (particle.oy - particle.y) * 0.01;
          particle.x += particle.vx;
          particle.y += particle.vy;
        }

        const fadePhase = isEnteringActive
          ? Math.max(0, transitionProgress - 0.52) / 0.48
          : 0;
        const activeAlpha = isEnteringActive
          ? Math.max(0, Math.min(0.92, particle.alpha + 0.28) * (1 - fadePhase * 1.18))
          : particle.alpha;
        const surgeWave = isEnteringActive
          ? 1 - (1 - transitionProgress) * (1 - transitionProgress)
          : 0;
        const drawSize = isEnteringActive
          ? particle.size * (1 + surgeWave * 1.18)
          : particle.size;

        context.globalAlpha = activeAlpha;
        context.fillStyle = particle.color;
        context.fillRect(
          particle.x - drawSize / 2,
          particle.y - drawSize / 2,
          drawSize,
          drawSize,
        );
      });

      context.restore();
      context.globalAlpha = 1;
      animationFrameId = window.requestAnimationFrame(drawParticles);
    }

    resizeCanvas();

    if (!reducedMotionQuery || !reducedMotionQuery.matches) {
      drawParticles();
    }

    function handleMotionChange() {
      if (!reducedMotionQuery) {
        return;
      }

      if (reducedMotionQuery.matches) {
        window.cancelAnimationFrame(animationFrameId);
        animationFrameId = 0;
        context.clearRect(0, 0, width, height);
        return;
      }

      if (!animationFrameId) {
        drawParticles();
      }
    }

    window.addEventListener("resize", resizeCanvas);

    if (reducedMotionQuery) {
      if (typeof reducedMotionQuery.addEventListener === "function") {
        reducedMotionQuery.addEventListener("change", handleMotionChange);
      } else if (typeof reducedMotionQuery.addListener === "function") {
        reducedMotionQuery.addListener(handleMotionChange);
      }
    }

    return function cleanupLoginBackgroundParticles() {
      isDisposed = true;
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", resizeCanvas);

      if (reducedMotionQuery) {
        if (typeof reducedMotionQuery.removeEventListener === "function") {
          reducedMotionQuery.removeEventListener("change", handleMotionChange);
        } else if (typeof reducedMotionQuery.removeListener === "function") {
          reducedMotionQuery.removeListener(handleMotionChange);
        }
      }
    };
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    const submitStartAt = Date.now();
    setIsEntering(true);
    setIsLoading(true);
    setErrorMessage("");

    try {
      const authResponse = await merchantLogin({
        username: account.trim(),
        password: password,
      });
      setStoredMerchantPortalToken(authResponse && authResponse.access_token);
      const merchantContext = await fetchMerchantContext();
      const session = buildMerchantSession({
        auth: authResponse,
        context: merchantContext,
      });

      const elapsed = Date.now() - submitStartAt;
      const remainingTransition = LOGIN_SUCCESS_TRANSITION_MS - elapsed;

      if (remainingTransition > 0) {
        await new Promise(function waitForTransition(resolve) {
          window.setTimeout(resolve, remainingTransition);
        });
      }

      login(session);
      history.push(
        (location.state && location.state.from && location.state.from.pathname) ||
          getMerchantDefaultRoute(session),
      );
    } catch (error) {
      setErrorMessage(
        getMerchantApiErrorMessage(
          error,
          "Merchant login failed. Please check your account and password.",
        ),
      );
      setIsEntering(false);
      clearStoredMerchantPortalSession();
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div
      className={
        isEntering
          ? "merchant-shell merchant-login-shell merchant-login-shell-portal merchant-login-shell-entering"
          : "merchant-shell merchant-login-shell merchant-login-shell-portal"
      }
    >
      <div className="merchant-login-backdrop" aria-hidden="true">
        {LOGIN_BACKGROUND_BLOBS.map(function renderBlob(style, index) {
          return (
            <div key={index} className="merchant-login-blob" style={style} />
          );
        })}
      </div>
      <canvas
        ref={canvasRef}
        className="merchant-login-particle-canvas"
        aria-hidden="true"
      />
      <div className="merchant-login-grain" aria-hidden="true" />
      <div className="merchant-login-wrap">
        <div className="merchant-login-brand">
          <div className="merchant-logo-mark merchant-login-logo-mark">
            <img
              src={process.env.PUBLIC_URL + "/merchant-login-logo.webp"}
              alt="Uniweb"
            />
          </div>
          <div className="merchant-login-brand-name">Uniweb Pay OS</div>
          <div className="merchant-login-brand-tag">Merchant Portal</div>
        </div>
        <form className="merchant-login-card" onSubmit={handleSubmit}>
          <div className="merchant-login-title">Welcome back</div>
          <div className="merchant-login-subtitle">
            Sign in to your merchant account to continue.
          </div>
          {errorMessage ? (
            <div className="merchant-login-error">{errorMessage}</div>
          ) : null}
          <MerchantLoginField
            id="merchant-account"
            label="Email Address"
            type="text"
            value={account}
            autoComplete="username"
            onChange={function handleAccountChange(event) {
              setAccount(event.target.value);
            }}
          />
          <MerchantLoginField
            id="merchant-password"
            label="Password"
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={function handlePasswordChange(event) {
              setPassword(event.target.value);
            }}
          />
          <button
            className={
              isLoading
                ? "merchant-login-button is-loading"
                : "merchant-login-button"
            }
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? "Signing In..." : "Sign In →"}
          </button>
          <div className="merchant-login-footer">
            © 2026 Uniweb Pte. Ltd.
            <br />
            Licensed Payment Institution · MAS Licence No. PS20200248
          </div>
        </form>
        <div className="merchant-login-meta">
          All connections are encrypted and secured
        </div>
        <div className="merchant-login-meta merchant-login-meta-secondary">
          {new Date().getFullYear()} Uniweb Pte. Ltd. All Right Reserved.
        </div>
      </div>
    </div>
  );
}
